import { SVGProps } from 'react';
import { Category } from 'common/type';
import {
  Bank,
  Family,
  Identity,
  Pension,
  Property,
  Subscriptions,
  Birthdays,
  Tax,
  Vehicle,
  Education,
  Health,
  Travel,
  Work,
  Pets,
  CustomFolder,
  BankColorable,
  FamilyColorable,
  IdentityColorable,
  PensionColorable,
  PropertyColorable,
  SubscriptionsColorable,
  TaxColorable,
  VehicleColorable,
  EducationColorable,
  HealthColorable,
  TravelColorable,
  WorkColorable,
  PetsColorable,
  CustomFolderColorable,
} from './designSystem/iconComponents';
import type { ColorableIconComponent } from './designSystem/iconComponents';

type CatergoryLogoMap = {
  [key: string]: (props: SVGProps<SVGSVGElement>) => JSX.Element
}
type IdToCategoryNameMap = {
  [key: number]: string;
}
export const mobileWidthLimit = 480;
export const tabletWidthLimit = 768;
export const desktopWidthLimit = 769; // Breakpoint for desktop web layout (tablets and laptops)
export const largeDesktopWidthLimit = 1200;
// Per-file upload ceiling, in bytes. MUST stay in step with the API's
// AWS_MAX_FILE_SIZE_IN_BYTES: that value is baked into the presigned S3 POST
// policy (S3Helper::getPresignedOptions), so a client that allows more than
// the policy does just gets an opaque S3 rejection instead of our own message.
// Raised from 20MB, which was too small for the things people genuinely keep
// in a document vault - surveyors' reports, mortgage packs, scanned bundles.
export const fileUploadSize = 52428800;
export const fileUploadSizeLabel = '50MB';
export const vehicleId = 11;
export const propertyId = 7;
export const linkPath = {
  documentTitle: '/document',
  documentName: '/document/name',
  documentScanning: '/document/scanning',
  documentUpload: '/document/upload',
  documentUploadDvla: '/document/dvla',
  documentVehicle: '/document/vehicle',
  documentRenewal: '/document/renewal',
  documentData: '/document/data',
  documentReview: '/document/review',
  documentSuccess: '/document/success',
  documentList: '/documents',
  searchDocumentPath: '/searchDocument',
  dashboard: '/dashboard',
  reminder: '/reminders',
  menuBar: '/menu',
  manageCategories: '/manage-categories',
  tutorial: '/tutorial',
  verify: '/verify',
  success: '/success',
  forgotPassword: '/forgot-password',
  resetPasword: '/reset-password/:token',
  login: '/login',
  signup: '/signup',
  terms: '/terms-and-condition',
  category: '/documents/viewCategory/:categoryId',
  birthdays: '/birthdays',
  birthdayDetail: '/birthdays/:documentId',
  birthdayDetailPath: '/birthdays',
  editDocuments: '/editDocuments/:documentId',
  dashboardCategory: '/documents/viewCategory',
  viewGroup: '/documents/viewGroup/:groupType/:groupId',
  viewGroupPath: '/documents/viewGroup',
  groupSelection: '/document/group-selection',
  editDocumentsID: '/editDocuments',
  editCategory: '/editDocuments/category',
  editNotes: '/editDocuments/notes',
  editData: '/editDocuments/data',
  userProfile: '/myProfile',
  privacyPolicy: '/privacyPolicy',
  aboutDoqit: '/aboutDoqit',
  faq: '/faq',
  learn: '/learn',
  learnClip: '/learn/:slug',
  offer: '/offer/:slug',
  firstDocument: '/first-document',
  vehicleList: '/vehicleList/:vehicleCategoryId',
  vehicleListPath: '/vehicleList',
  mfa: '/mfa',
  pricing: '/pricing',
  vehicleSelection: '/document/vehicle-selection',
  manualVehicleEntry: '/document/manual-vehicle',
  addVehicle: '/vehicle/add',
  editVehicle: '/vehicle/edit/:vehicleId',
  // Sharing
  mySharing: '/my-sharing',
  mySharedLinks: '/my-shared-links',
  addSharingAccount: '/my-sharing/add',
  pendingRequests: '/my-sharing/pending',
  sharedWithMe: '/shared-with-me',
  sharedDocument: '/shared-document/:shareId',
  sharedDocumentByDoc: '/shared-document/doc/:documentId',
  sharingTab: '/sharing-tab',
  // Property
  propertySelection: '/document/property-selection',
  manualPropertyEntry: '/document/manual-property',
  propertyDetails: '/document/property-details',
  propertyList: '/propertyList/:propertyCategoryId',
  propertyListPath: '/propertyList',
  addProperty: '/property/add',
  editProperty: '/property/edit/:propertyId',
  // Onboarding
  onboardingWhoFor: '/onboarding/who-for',
  onboardingCareAbout: '/onboarding/care-about',
  onboardingStories: '/onboarding/stories',
  onboardingCost: '/onboarding/cost',
  onboardingHowWeHelp: '/onboarding/how-we-help',
  onboardingThreeSteps: '/onboarding/three-steps',
  onboardingPrivacy: '/onboarding/privacy',
  onboardingSocialProof: '/onboarding/social-proof',
  onboardingAddItem: '/onboarding/add-item',
  onboardingScanning: '/onboarding/scanning',
  onboardingSampleScan: '/onboarding/sample-scan',
  onboardingDoqReady: '/onboarding/doq-ready',
  onboardingDocDetail: '/onboarding/doc-detail',
  onboardingNameItem: '/onboarding/name-item',
  onboardingCategory: '/onboarding/category',
  onboardingReminder: '/onboarding/reminder',
  // Dev-only workbench (see routes.tsx's NODE_ENV guard) — never registered in production builds.
  onboardingSampleGallery: '/onboarding/sample-gallery',
};

// The home-screen "Scan a document" shortcut (doqit-app://shortcut/scan,
// handled in AppLifecycleHandler.tsx) queues linkPath.documentUpload with
// this marker in the query string so UploadFile.tsx knows to auto-invoke
// the scanner once it mounts. Shared here so both sides read/write the same
// literal instead of two independent string constants drifting apart.
export const shortcutScanQueryParam = 'shortcut';
export const shortcutScanQueryValue = 'scan';

export const categoryIcons: CatergoryLogoMap = {
  Bank,
  // Alias for the Bank → Finance display rename — keeps name-based icon
  // lookup working when the rename pushes "Finance" through as the name.
  Finance: Bank,
  Family,
  Identity,
  Pension,
  Property,
  Subscriptions,
  Birthdays,
  Tax,
  Vehicle,
  Education,
  Health,
  Travel,
  Work,
  Pets,
  CustomFolder,
};

// Colorable versions of category icons (accept optional color prop)
export const categoryColorableIcons: Record<string, ColorableIconComponent> = {
  Bank: BankColorable,
  Finance: BankColorable,
  Family: FamilyColorable,
  Identity: IdentityColorable,
  Pension: PensionColorable,
  Property: PropertyColorable,
  Subscriptions: SubscriptionsColorable,
  Tax: TaxColorable,
  Vehicle: VehicleColorable,
  Education: EducationColorable,
  Health: HealthColorable,
  Travel: TravelColorable,
  Work: WorkColorable,
  Pets: PetsColorable,
  CustomFolder: CustomFolderColorable,
};

export const initialCategoryState: Category[] = [
  {
    id: 1,
    name: 'Finance',
    slug: 'bank',
    image: '',
  },
  {
    id: 2,
    name: 'Education',
    slug: 'education',
    image: '',
  },
  {
    id: 3,
    name: 'Family',
    slug: 'family',
    image: '',
  },
  {
    id: 5,
    name: 'Health',
    slug: 'health',
    image: '',
  },
  {
    id: 4,
    name: 'Identity',
    slug: 'identity',
    image: '',
  },
  {
    id: 6,
    name: 'Pension',
    slug: 'pension',
    image: '',
  },
  {
    id: 7,
    name: 'Property',
    slug: 'property',
    image: '',
  },
  {
    id: 9,
    name: 'Subscriptions',
    slug: 'subscriptions',
    image: '',
  },
  {
    id: 8,
    name: 'Tax',
    slug: 'tax',
    image: '',
  },
  {
    id: 10,
    name: 'Travel',
    slug: 'travel',
    image: '',
  },
  {
    id: 11,
    name: 'Vehicle',
    slug: 'vehicle',
    image: '',
  },
  {
    id: 12,
    name: 'Work',
    slug: 'work',
    image: '',
  },
  {
    id: 13,
    name: 'Pets',
    slug: 'pets',
    image: '',
  },
];
/* eslint-disable @typescript-eslint/naming-convention */
export const categoryIdToNameMap: IdToCategoryNameMap = {
  1: 'Bank',
  2: 'Education',
  3: 'Family',
  5: 'Health',
  4: 'Identity',
  6: 'Pension',
  7: 'Property',
  9: 'Subscriptions',
  8: 'Tax',
  10: 'Travel',
  11: 'Vehicle',
  12: 'Work',
  13: 'Pets',
};
/* eslint-enable @typescript-eslint/naming-convention */

// Map DVLA colour names to hex color codes for vehicle icons
export const dvlaColorToHex: { [key: string]: string } = {
  // Common DVLA colours
  black: '#1F2937',
  white: '#F9FAFB',
  silver: '#9CA3AF',
  grey: '#6B7280',
  gray: '#6B7280',
  blue: '#3B82F6',
  red: '#EF4444',
  green: '#22C55E',
  yellow: '#EAB308',
  orange: '#F97316',
  brown: '#92400E',
  beige: '#D4A574',
  cream: '#FFFDD0',
  gold: '#D4AF37',
  bronze: '#CD7F32',
  maroon: '#800000',
  burgundy: '#800020',
  purple: '#9333EA',
  pink: '#EC4899',
  turquoise: '#06B6D4',
  // Default fallback (purple - our app theme color)
  default: '#9333EA',
};

// Convert DVLA colour name to hex code
export const getDvlaColorHex = (colorName: string | undefined): string => {
  if (!colorName) return dvlaColorToHex.default;
  const normalizedColor = colorName.toLowerCase().trim();
  return dvlaColorToHex[normalizedColor] || dvlaColorToHex.default;
};

// Demo/bypass accounts — these skip MFA + subscription gating.
// SINGLE SOURCE OF TRUTH. Do not re-inline this list anywhere.
// Union of all previously divergent inline lists (see refactor commit).
// Single source of truth for the demo/bypass account that skips MFA +
// subscription checks. Compared case-insensitively. Per product decision,
// only dan@addieapp.com bypasses (the previously-scattered list of 5 was
// deliberately narrowed to this one address).
export const BYPASS_EMAILS: readonly string[] = [
  'dan@addieapp.com',
];

// eslint-disable-next-line arrow-body-style
export const isBypassEmail = (email?: string | null): boolean => !!email && BYPASS_EMAILS.includes(email.toLowerCase().trim());
